import Tabs from "components/EvtKit/tabs/Tabs"
import TabsContent from "components/EvtKit/tabs/TabsContent"
import { memo, useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import styled from "styled-components"
import Benefits from "./components/v2/Benefits"
import DevModePricingVariables from "./components/v2/DevModePricingVariables"
import PageLayout from "components/PageLayout"
import PlanSummary from "./components/v2/PlanSummary"
import PurchasePlanForm from "./components/v2/PurchasePlanForm"
import SimplePricingVariablesList from "./components/v2/SimplePricingVariablesList"
import CreatePlanTabList from "./components/v2/tabs/CreatePlanTabList"
import { planFormHeadingText, tabIds } from "./constants/defaultValues"
import usePurchasePlan from "./hooks/usePurchasePlan"
import { usePurchasePlanIdentifiers } from "./hooks/usePurchasePlanIdentifiers"
import usePurchasePlanRequest from "./hooks/usePurchasePlanRequest"
import { mapPurchasePlanToSubmition } from "./mappers/mapPurchasePlanForm"

const ContentContainer = styled.div`
  display: flex;
  width: 98%;
  gap: 8px;
  margin: 43px 0 20px 0;
`

const CardContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: ${(props) => props.theme.palette.white};
  padding: 16px 16px 0px 16px;
  box-shadow: 0px 2px 6px 0px ${(props) => props.theme.palette.box_shadow};
`

const PurchasePlanCreation = () => {
  const {
    isValidPurchasePlan,
    purchasePlanSummary,
    purchasePlanForm,
    benefits,
    pricingVariables,
    isDevMode,
    setDevModePricingVariablesToSubmition,
    devModePricing
  } = usePurchasePlan()

  const navigate = useNavigate()

  const { identifiersDefinition, identifiersCleanup } = usePurchasePlanIdentifiers()

  const navigateToPurchasePlanPage = useCallback(() => {
    navigate("/purchase-plans")
  }, [])

  const { onSubmit } = usePurchasePlanRequest(
    "create",
    mapPurchasePlanToSubmition(
      purchasePlanForm.data,
      benefits.benefitsList,
      pricingVariables.purchasePlanPricing,
      pricingVariables.pricingVariablesList,
      devModePricing,
      pricingVariables.pricingUnit
    ),
    navigateToPurchasePlanPage,
    "Erro ao criar plano. Verifique se preencheu todos os dados obrigatórios corretamente e se o limite de caracteres está compatível com o limite estipulado"
  )

  const onProductChange = useCallback((productId: string) => {
    pricingVariables.resetList()

    identifiersDefinition(productId, "")
  }, [])

  const toggleDevMode = useCallback(() => {
    const { handlePricingChange, purchasePlanPricing } = pricingVariables

    handlePricingChange("isSimplifiedPricing", !purchasePlanPricing.isSimplifiedPricing)

    handlePricingChange("fromPriceUnit", "")
  }, [pricingVariables])

  useEffect(() => {
    return () => {
      identifiersCleanup()
    }
  }, [])

  return (
    <PageLayout title="Planos">
      <ContentContainer>
        <Tabs defaultActiveTabId={tabIds.plans}>
          <CardContainer>
            <CreatePlanTabList
              isBenefitStepDisabled={!isValidPurchasePlan}
              toggleDevMode={toggleDevMode}
              isDevMode={isDevMode}
            />

            <TabsContent triggerId={tabIds.plans}>
              <PurchasePlanForm
                {...purchasePlanForm}
                headingText={planFormHeadingText.create}
                onProductChange={onProductChange}
                pricingUnit={pricingVariables.pricingUnit}
                isDevMode={isDevMode}
              >
                <SimplePricingVariablesList
                  {...pricingVariables}
                  isDevMode={isDevMode}
                  selectedProduct={purchasePlanForm.data.product}
                />
                <DevModePricingVariables
                  isDevMode={isDevMode}
                  setDevModePricingVariablesToSubmition={setDevModePricingVariablesToSubmition}
                />
              </PurchasePlanForm>
            </TabsContent>

            <TabsContent triggerId={tabIds.benefits}>
              <Benefits {...benefits} onSubmit={onSubmit} />
            </TabsContent>
          </CardContainer>

          <PlanSummary {...purchasePlanSummary} />
        </Tabs>
      </ContentContainer>
    </PageLayout>
  )
}

export default memo(PurchasePlanCreation)
